import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import ProfileContainer from '@bridebook/web/app-basic/components/supplier/profile-container';
import { setAlternateLinks, setCanonicalLink } from '@bridebook/web/app-basic/lib/app/app-slice';
import { getIsTablet } from '@bridebook/web/app-basic/lib/app/selectors';
import { setArticlesSupport } from '@bridebook/web/app-basic/lib/article/article-slice';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import {
  getIsSupplierDeleted,
  getSupplier,
  getSupplierErrorCode,
  getSupplierFeedbackCount,
  getSupplierPhotos,
  getSupplierReviews,
  getSupplierSeoIndex,
  getSupplierType,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import {
  fetchSupplierError,
  saveFeedbackDetails,
  saveInstagramMedia,
  saveSupplierDetails,
} from '@bridebook/web/app-basic/lib/supplier/supplier-details-slice';
import { saveSupplier } from '@bridebook/web/app-basic/lib/supplier/supplier-slice';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { isEmpty } from 'ramda';
import 'react-alice-carousel/lib/alice-carousel.css';
import { batch } from 'react-redux';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { getSupplierUrl, mapUrlSlugToSupplierType } from '@bridebook/toolbox/src';
import { HttpStatusCode } from '@bridebook/toolbox/src/api/http-status';
import gazetteer from '@bridebook/toolbox/src/gazetteer';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { decodePublicSupplierId } from '@bridebook/toolbox/src/hashids';
import type { UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { Box, Col, Container, Row } from '@bridebook/ui';
import Overlay from '@bridebook/web/app-shared/components/overlay/overlay';
import { INSTAGRAM_MAX_ITEMS } from '@bridebook/web/app-shared/components/supplier/sections/instagram-widget/instagram-grid-item';
import SupplierMicrodata from '@bridebook/web/app-shared/components/supplier/sections/supplier-microdata';
import sharedComponentStyles from '@bridebook/web/app-shared/components/supplier/supplier.style';
import { getArticlesMarketSupport } from '@bridebook/web/app-shared/lib/articles/get-articles-market-support';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { REVIEWS_PER_PAGE_LOGGED_OUT } from '@bridebook/web/app-shared/lib/supplier/constants';
import {
  fetchSupplierBasicData,
  fetchSupplierExtendedData,
  fetchSupplierFeedback,
} from '@bridebook/web/app-shared/lib/supplier/fetchers';
import { InstagramMedia } from '@bridebook/web/app-shared/lib/supplier/types';
import {
  getMetaDesc,
  getSupplierMeta,
  getVenueTitle,
} from '@bridebook/web/app-shared/lib/supplier/utils';
import { fetchLatestFromInstagramCached } from '@bridebook/web/app-shared/lib/supplier/utils/get-instagram-media';
import { getIsSupplierPreview } from '@bridebook/web/app-shared/lib/supplier/utils/get-is-supplier-preview';
import { isValidForMarket } from '@bridebook/web/app-shared/lib/supplier/utils/is-valid-for-market';
import { getBreadcrumbsLocation } from '@bridebook/web/app-shared/lib/utils';
import { translateRecursive } from '@bridebook/web/app-shared/lib/utils/translate-recursive';
import { AppType } from '@bridebook/web/pages/_app';
import ErrorPage from '@bridebook/web/pages/_error';
import { buildUISupplier } from '@bridebook/web/lib/shortlist/utils';
import { getSupplierThumb } from '@bridebook/web/lib/supplier/utils/get-supplier-thumb';
import { getAlternateLinksForSupplier } from '@bridebook/web/lib/utils/i18n-routing/get-alternate-links';
import { getLocalisedCanonical } from '@bridebook/web/lib/utils/i18n-routing/get-localised-canonical';
import { useHandleShowroundRedirect } from '@bridebook/web/app-basic/lib/hooks/use-handle-showround-redirect';
import { useIsVenueProfileV2 } from '@bridebook/web/app-basic/components/supplier/hooks/use-is-venue-profile-v2';
import { getTranslationTag } from '@bridebook/models/source/helpers/translate';
import {
  createFullQuery,
  fetchLocationData,
  fetchSearchResults,
} from '@bridebook/web/app-shared/lib/search/utils';
import { ISearchQuery } from '@bridebook/web/app-shared/lib/search/types';
import {
  saveLocationData,
  saveSearchResult,
  startSearch,
} from '@bridebook/web/app-basic/lib/search/search-slice';

interface IProps {
  canonicalUrl: string;
}

const Supplier: NextPage<IProps> = ({ canonicalUrl }: IProps) => {
  const photos = useSelector(getSupplierPhotos);
  const router = useRouter();
  const supplier = useSelector(getSupplier);
  const supplierType = useSelector(getSupplierType);
  const seoIndex = useSelector(getSupplierSeoIndex);
  const supplierErrorCode = useSelector(getSupplierErrorCode);
  const isSupplierDeleted = useSelector(getIsSupplierDeleted);
  const reviews = useSelector(getSupplierReviews);
  const reviewsCount = useSelector(getSupplierFeedbackCount);
  const isTablet = useSelector(getIsTablet);

  const { query } = router;
  const market = useMarket();
  const isSupplierPreview = getIsSupplierPreview(query);

  // All hooks must be called before any early returns
  // Redirects to login if URL contains 'schedule' or 'cancel' query parameters
  useHandleShowroundRedirect({
    isVenue: supplierType === 'venue',
  });
  const isVenueProfileV2 = useIsVenueProfileV2();

  const width = isVenueProfileV2 ? '1045px' : undefined;
  // Check if URL slug matches supplier type (eg. /wedding-photographers/:id
  // for a venue should return 404)
  const isValidUrlSlug =
    supplierType === mapUrlSlugToSupplierType((query.slug?.toString() || '') as UrlFriendlySlug);

  if (supplierErrorCode) {
    return <ErrorPage statusCode={supplierErrorCode} />;
  }

  if (isSupplierDeleted || isEmpty(supplier) || !isValidUrlSlug || !supplier) {
    return <ErrorPage statusCode={404} />;
  }

  const uiSupplier = buildUISupplier(supplier);
  const { town, county, country, type, name, lat, lng } = uiSupplier;

  const address = supplier?.address?.street?.[0];
  const postcode = supplier?.address?.postalCode;
  const customTitle = supplier.seo?.profilePageTitle;
  const title = getVenueTitle({ name, category: type, market, customTitle, isLoggedIn: false });
  const metas = getSupplierMeta({
    town,
    county,
    lat,
    lng,
    name,
    photos,
    address,
    postcode,
    country,
    title,
  });
  const metaDesc = getMetaDesc(uiSupplier);
  const imageUrl = getSupplierThumb(buildUISupplier(supplier), photos);

  const breadcrumbsData = type
    ? getBreadcrumbsLocation({
        locationData: { country, county, town },
        slug: type,
        name,
        isLoggedIn: false,
        market,
        countryCode: uiSupplier?.countryCode,
      })
    : [];

  const sharedStyles = sharedComponentStyles();

  const isDefaultMarketLocale =
    gazetteer.getMarketByCountry(market.country).locale === market.locale;
  const isProfileTranslated =
    isDefaultMarketLocale || getTranslationTag(supplier, market.locale) !== null;
  const isIndexable = market.getLocaleFlags()?.indexable === true && isProfileTranslated;

  return (
    <>
      <Head
        title={title}
        isIndexable={isIndexable}
        seoIndex={seoIndex}
        ogImage={imageUrl}
        ogTitle={title}
        description={metaDesc}>
        <meta property="og:description" content={metaDesc} />
        {metas.map((item) => (
          <meta key={`meta-${item.property}`} property={item.property} content={item.content} />
        ))}
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:description" content={metaDesc} />
      </Head>
      <SupplierMicrodata
        supplier={supplier}
        photos={photos}
        canonicalUrl={canonicalUrl}
        metaDesc={metaDesc}
        reviews={reviews}
        reviewsCount={reviewsCount}
      />
      {isSupplierPreview ? <Overlay /> : <TopBar />}
      <Box style={sharedStyles.contentWrap} data-name={`supplier-${type}`}>
        <meta itemProp="name" content={name} />
        <Container style={width ? { ...sharedStyles.container, width } : sharedStyles.container}>
          <Row>
            <Col xs={12} md={isTablet || width ? 12 : 8} lg={isTablet || width ? 12 : 8}>
              <ProfileContainer breadcrumbsData={breadcrumbsData} />
            </Col>
          </Row>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export const getServerSideProps = basicGSSPWrapper<IProps>(
  [
    'supplier',
    'supplierCard',
    'offers',
    'realWeddings',
    'filters',
    'loggedOutSearchBar',
    'signup',
    'articles',
  ],
  async ({ store, initialAppState, context }) => {
    const { query, params } = context;
    const { market } = initialAppState;
    const { dispatch } = store;
    const id = query.id as string;

    let validForMarket = true;

    try {
      const { supplierId, numbers } = decodePublicSupplierId(id);

      if (numbers.length === 0) {
        throw new Error(`Wrong supplier id: ${supplierId}`);
      }

      const [supplier, feedback, supplierData, articlesSupport] = await Promise.all([
        fetchSupplierBasicData(supplierId).catch((error) => {
          dispatch(fetchSupplierError(error.status));
          if (error.status !== HttpStatusCode.OK) context.res.statusCode = error.status;
          throw error;
        }),
        fetchSupplierFeedback(supplierId, REVIEWS_PER_PAGE_LOGGED_OUT),
        fetchSupplierExtendedData(supplierId),
        getArticlesMarketSupport(market.prefix),
      ]);

      const slug = (params?.slug || '') as UrlFriendlySlug;
      const area = (supplier?.address?.adminArea?.[0] || '').toLowerCase();

      const searchQuery: ISearchQuery = {
        slug,
        area,
        sort: 'seoScore',
        size: 7,
      };
      const fullQuery = createFullQuery(searchQuery);

      try {
        const { list } = store.getState().search;

        const locationData = await fetchLocationData({ area, market, slug });

        if (locationData) {
          store.dispatch(saveLocationData({ area, locationData }));
        }

        if (isEmpty(list)) {
          store.dispatch(startSearch());
          const { results } = await fetchSearchResults(locationData, fullQuery, market, false);

          if (results) {
            store.dispatch(saveSearchResult(results));
          }
        }
      } catch (error) {
        SentryMinimal().captureException(error);
      }

      let instagramMedia: InstagramMedia[] | null = null;

      if ((supplierData.appsConnected || []).includes('instagram')) {
        instagramMedia = await fetchLatestFromInstagramCached({
          supplierId,
          limit: INSTAGRAM_MAX_ITEMS.mobile,
        }).catch((error) => {
          SentryMinimal().captureException(error);
          // Do not prevent the page from loading if Instagram API fails
          return null;
        });
      }

      if (supplierData.deleted) {
        context.res.statusCode = 404;
      }

      batch(() => {
        dispatch(saveSupplier(translateRecursive(supplier, market.locale)));
        dispatch(saveSupplierDetails(translateRecursive(supplierData, market.locale)));
        dispatch(saveFeedbackDetails(translateRecursive(feedback, market.locale)));
        dispatch(saveInstagramMedia(instagramMedia));
        dispatch(setArticlesSupport(articlesSupport));
      });

      const country = supplier?.address?.country;
      validForMarket = isValidForMarket({
        supplierCountry: country,
        market,
        isLoggedIn: false,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Supplier page error', error);
    }

    const supplier = getSupplier(store.getState()) as ISupplier;
    const supplierUrl = getSupplierUrl(supplier);

    // Check if full URL matches how it should look like
    // If not, then redirect to a valid URL to avoid duplicated content.
    if (
      params &&
      typeof params.id === 'string' &&
      `/wedding-${params.slug}/${encodeURI(params.id)}` !== supplierUrl
    ) {
      return {
        redirect: {
          permanent: true,
          destination: `/${market.prefix}${supplierUrl}`,
        },
      };
    }

    const pathname = getSupplierUrl(supplier);
    const canonicalUrl = getLocalisedCanonical(market, pathname);
    store.dispatch(setCanonicalLink(canonicalUrl));

    const supplierLocales: string[] = Object.keys(supplier._translations || {});
    if (supplierLocales.length) {
      // LIVE-21319 Only for a profile that has already been translated. We do not want hreflang tags across the profiles that are both in the French language.
      const alternate = getAlternateLinksForSupplier(pathname, market, supplierLocales);
      store.dispatch(setAlternateLinks(alternate));
    }

    return {
      props: {
        canonicalUrl,
      },
      notFound: !validForMarket,
    };
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.HOUR,
      sMaxAge: EdgeCacheTTL.HOUR,
      staleWhileRevalidate: EdgeCacheTTL.MONTH,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.TEN_MINUTES,
      sMaxAge: EdgeCacheTTL.TEN_MINUTES,
      staleWhileRevalidate: EdgeCacheTTL.DAY,
    },
  },
);

Supplier.AppType = AppType.Basic;

export default Supplier;
